import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios';
import config from './config';
import { resolveApiBaseUrl } from './resolveApiBaseUrl';

import { ErrorResponse } from 'types/ResponseTypes';

const { api } = config;

axios.defaults.baseURL =
  resolveApiBaseUrl(process.env.REACT_APP_POS2U_API_URL) || api.API_URL;

axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true;

// const errStatus = [500, 401, 404];

const authUser: any = sessionStorage.getItem('authUser');
const token = JSON.parse(authUser) ? JSON.parse(authUser).access_token : null;
if (token) axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;

axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ErrorResponse>) => {
    if (error.response) {
      const { status, data } = error.response;
      let message = data?.message || 'An unknown error occurred';

      switch (status) {
        case 400:
          message = 'Bad Request: ' + (data?.message || '');
          break;
        case 401:
          message = 'Unauthorized: ' + (data?.message || '');
          break;
        case 403:
          message = 'Forbidden: ' + (data?.message || '');
          break;
        case 404:
          message = 'Resource not found';
          break;
        case 422: {
          message = data?.detail
            ? 'Validation error: ' + JSON.stringify(data?.detail || {})
            : 'Validation error';
          break;
        }
        case 429: {
          message = data?.message
            ? data.message
            : 'Too many login attempts, please try again later.';
          break;
        }
        case 500:
          message = 'Internal server error';
          break;
        default:
          message = 'Unexpected error';
      }
      return Promise.reject({ status, message, data });
    }

    if (error.request) {
      return Promise.reject('No response received from the server');
    }

    return Promise.reject(error.message || 'Unknown error');
  }
);

const setAuthorization = (token: string) => {
  setLanguage();
  axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
};

const setLanguage = () => {
  const i18nLanguage = localStorage.getItem('I18N_LANGUAGE');
  axios.defaults.headers.common['Accept-Language'] = i18nLanguage;
};

class APIClient {
  get = (url: string, params?: any): Promise<any> => {
    let response: Promise<AxiosResponse>;

    const paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map((key) => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString =
        paramKeys && paramKeys.length ? paramKeys.join('&') : '';
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  create = (url: string, data: any): Promise<any> => {
    return axios.post(url, data);
  };

  update = (url: string, data: any): Promise<any> => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any): Promise<any> => {
    return axios.put(url, data);
  };

  delete = (
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };
}

export { APIClient, setAuthorization, setLanguage };
